import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon, Clock, Check } from 'lucide-react';
import { format } from 'date-fns';
import { packages } from '../data/mockData';
import { toast } from 'sonner';
import { useLanguage } from '../translations/LanguageContext';

const FORMSPREE_BOOKING_ENDPOINT = 'https://formspree.io/f/xvzkkjla';

const Booking = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [date, setDate] = useState();
  const [selectedPackage, setSelectedPackage] = useState(
    location.state?.selectedPackage || packages[1].id
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    childName: '',
    childAge: '',
    parentName: '',
    email: '',
    phone: '',
    guests: '',
    time: '',
    specialRequests: '',
  });

  const selectedPkg = packages.find((p) => p.id === selectedPackage);
  const grandTotal = selectedPkg?.price || 0;

  // Generate time options from 12:00 to 22:00
  const timeOptions = [];
  for (let hour = 12; hour <= 21; hour++) {
    timeOptions.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 21) {
      timeOptions.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(t('booking.selectDate') || 'Please select a party date');
      return;
    }

    if (!formData.time) {
      toast.error(t('booking.selectTime') || 'Please select a party time');
      return;
    }

    setIsSubmitting(true);

    const bookingData = {
      package: `${selectedPkg?.name} - €${selectedPkg?.price}`,
      date: format(date, 'PP'),
      time: formData.time,
      childName: formData.childName,
      childAge: formData.childAge,
      parentName: formData.parentName,
      email: formData.email,
      phone: formData.phone,
      guests: formData.guests,
      total: `€${grandTotal}`,
      specialRequests: formData.specialRequests || 'None',
    };

    try {
      const response = await fetch(FORMSPREE_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        toast.success(t('booking.success') || 'Booking request sent successfully! We will contact you soon.');
        setFormData({
          childName: '',
          childAge: '',
          parentName: '',
          email: '',
          phone: '',
          guests: '',
          time: '',
          specialRequests: '',
        });
        setDate(undefined);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error(t('booking.error') || 'Failed to send booking. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2e5453]">
      {/* Hero */}
      <section className="bg-[#3d6a68] py-12">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] mb-4">
            {t('booking.title') || 'BOOK YOUR PARTY'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#96ba1e]">{t('booking.heading') || "Book a Magical Party!"}</h1>
          <p className="text-xl text-[#c4d88f]">{t('booking.subtitle') || "Let's make your child's birthday unforgettable"}</p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 bg-[#2e5453]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Package Selection */}
                <Card className="bg-[#3d6a68] border-[#96ba1e] border">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4 text-[#96ba1e]">{t('booking.step1') || '1. Select Package'}</h2>
                    <div className="grid md:grid-cols-3 gap-4">
                      {packages.map((pkg) => (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => setSelectedPackage(pkg.id)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            selectedPackage === pkg.id
                              ? 'border-[#96ba1e] bg-[#2e5453]'
                              : 'border-[#4a7a79] hover:border-[#96ba1e] bg-[#2e5453]'
                          }`}
                        >
                          {selectedPackage === pkg.id && (
                            <Check className="w-5 h-5 text-[#96ba1e] float-right" />
                          )}
                          <div className="font-bold text-lg text-[#96ba1e]">{pkg.name}</div>
                          <div className="text-2xl font-bold text-[#96ba1e]">€{pkg.price}</div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Date & Time */}
                <Card className="bg-[#3d6a68] border-[#96ba1e] border">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4 text-[#96ba1e]">{t('booking.step2') || '2. Pick Date & Time'}</h2>
                    <div className="bg-[#2e5453] border border-[#96ba1e] rounded-lg p-3 mb-4">
                      <p className="text-[#96ba1e] text-sm font-medium">
                        🕐 {t('booking.workingHours') || 'Working hours: 12:00 - 22:00 (including weekends)'}
                      </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#96ba1e]">{t('booking.partyDate') || 'Party Date'} *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] hover:bg-[#3d6a68]"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-[#96ba1e]" />
                              {date ? format(date, 'PPP') : (t('booking.selectDatePlaceholder') || 'Select date')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#2e5453] border-[#96ba1e]">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              disabled={(date) => date < new Date()}
                              initialFocus
                              className="bg-[#2e5453]"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="time" className="text-[#96ba1e]">{t('booking.partyTime') || 'Party Time'} *</Label>
                        <div className="relative mt-2">
                          <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#96ba1e]" />
                          <select
                            id="time"
                            value={formData.time}
                            onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border rounded-md bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] focus:outline-none focus:ring-2 focus:ring-[#96ba1e]"
                            required
                          >
                            <option value="">{t('booking.selectTimePlaceholder') || 'Select time'}</option>
                            {timeOptions.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Party Details */}
                <Card className="bg-[#3d6a68] border-[#96ba1e] border">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4 text-[#96ba1e]">{t('booking.step3') || '3. Party Details'}</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="childName" className="text-[#96ba1e]">{t('booking.childName') || "Birthday Child's Name"} *</Label>
                        <Input
                          id="childName"
                          value={formData.childName}
                          onChange={(e) => setFormData({ ...formData, childName: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="childAge" className="text-[#96ba1e]">{t('booking.childAge') || "Child's Age"} *</Label>
                        <Input
                          id="childAge"
                          type="number"
                          min="1"
                          max="18"
                          value={formData.childAge}
                          onChange={(e) => setFormData({ ...formData, childAge: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="parentName" className="text-[#96ba1e]">{t('booking.parentName') || 'Parent/Guardian Name'} *</Label>
                        <Input
                          id="parentName"
                          value={formData.parentName}
                          onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-[#96ba1e]">{t('booking.phone') || 'Phone Number'} *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#96ba1e]">{t('booking.email') || 'Email Address'} *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guests" className="text-[#96ba1e]">{t('booking.guests') || 'Number of Guests'} *</Label>
                        <Input
                          id="guests"
                          type="number"
                          min="1"
                          value={formData.guests}
                          onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="specialRequests" className="text-[#96ba1e]">{t('booking.specialRequests') || 'Special Requests (Optional)'}</Label>
                      <textarea
                        id="specialRequests"
                        value={formData.specialRequests}
                        onChange={(e) =>
                          setFormData({ ...formData, specialRequests: e.target.value })
                        }
                        className="w-full mt-2 p-3 border rounded-lg h-24 resize-none bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        placeholder={t('booking.specialRequestsPlaceholder') || 'Any dietary restrictions, theme preferences, or special requests...'}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Summary */}
              <div className="lg:col-span-1">
                <Card className="sticky top-20 bg-[#3d6a68] border-[#96ba1e] border">
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-2xl font-bold text-[#96ba1e]">{t('booking.summary') || 'Booking Summary'}</h2>
                    
                    <div className="space-y-3 py-4 border-y border-[#96ba1e]/30">
                      <div className="flex justify-between">
                        <span className="text-[#c4d88f]">{t('booking.packageLabel') || 'Package'}:</span>
                        <span className="font-semibold text-[#96ba1e]">{selectedPkg?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#c4d88f]">{t('booking.priceLabel') || 'Price'}:</span>
                        <span className="font-semibold text-[#96ba1e]">€{selectedPkg?.price}</span>
                      </div>
                      {date && (
                        <div className="flex justify-between">
                          <span className="text-[#c4d88f]">{t('booking.dateLabel') || 'Date'}:</span>
                          <span className="font-semibold text-[#96ba1e]">{format(date, 'PP')}</span>
                        </div>
                      )}
                      {formData.time && (
                        <div className="flex justify-between">
                          <span className="text-[#c4d88f]">{t('booking.timeLabel') || 'Time'}:</span>
                          <span className="font-semibold text-[#96ba1e]">{formData.time}</span>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-[#96ba1e]/30">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-xl font-bold text-[#96ba1e]">{t('booking.total') || 'Total'}:</span>
                        <span className="text-3xl font-bold text-[#96ba1e]">€{grandTotal}</span>
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453] py-6 text-lg font-bold"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (t('booking.sending') || 'Sending...') : (t('booking.submitButton') || 'Send Booking Request')}
                      </Button>
                    </div>

                    <p className="text-xs text-[#c4d88f] text-center">
                      {t('booking.confirmation') || 'We will contact you within 24 hours to confirm your booking'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
